import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, CornerDownLeft } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ADMIN_MODULES, MAIN_MODULES, QUICK_CREATE } from "@/lib/modules";

const useSearch = (query) => {
  const [state, setState] = useState({ loading: false, groups: [] });
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setState({ loading: false, groups: [] });
      return undefined;
    }
    setState((s) => ({ ...s, loading: true }));
    const t = setTimeout(() => {
      api.get("/search", { params: { q } })
        .then((r) => setState({ loading: false, groups: r.data.groups }))
        .catch(() => setState({ loading: false, groups: [] }));
    }, 250);
    return () => clearTimeout(t);
  }, [query]);
  return state;
};

const matches = (label, q) => !q || label.toLowerCase().includes(q.toLowerCase());

export const CommandPalette = ({ open, onOpenChange, onCreate }) => {
  const [query, setQuery] = useState("");
  const { can } = useAuth();
  const navigate = useNavigate();
  const { loading, groups } = useSearch(query);

  useEffect(() => { if (!open) setQuery(""); }, [open]);

  const go = (path) => { onOpenChange(false); navigate(path); };
  const nav = [...MAIN_MODULES, ...ADMIN_MODULES].filter((m) => (!m.perm || can(m.perm)) && matches(m.label, query));
  const actions = QUICK_CREATE.filter((a) => can(a.perm) && matches(a.label, query));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-[18%] max-w-xl translate-y-0 overflow-hidden p-0" data-testid="command-palette">
        <DialogTitle className="sr-only">Ricerca globale</DialogTitle>
        <Command shouldFilter={false} className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-slate-400">
          <CommandInput value={query} onValueChange={setQuery} placeholder="Cerca clienti, contatti, offerte, interventi, documenti…" data-testid="command-palette-input" />
          <CommandList className="max-h-[420px]">
            {loading && <div className="flex items-center gap-2 px-4 py-3 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Ricerca in corso…</div>}
            {!loading && query.trim().length >= 2 && <CommandEmpty data-testid="command-palette-empty">Nessun risultato per “{query}”.</CommandEmpty>}
            {groups.map((g) => (
              <CommandGroup key={g.type} heading={g.label} data-testid={`search-group-${g.type}`}>
                {g.items.map((it) => (
                  <CommandItem key={it.id} value={`${g.type}-${it.id}`} onSelect={() => go(it.path)} data-testid={`search-result-${g.type}-${it.id}`}>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-slate-800">{it.title}</p>
                      {it.subtitle && <p className="truncate text-xs text-slate-500">{it.subtitle}</p>}
                    </div>
                    {it.is_demo && <span className="ml-auto text-[10px] font-semibold uppercase text-amber-600">Demo</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
            {nav.length > 0 && (
              <CommandGroup heading="Navigazione">
                {nav.map(({ id, label, path, icon: Icon }) => (
                  <CommandItem key={id} value={`nav-${id}`} onSelect={() => go(path)} data-testid={`search-nav-${id}`}>
                    <Icon className="mr-2 h-4 w-4 text-slate-400" />{label}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {actions.length > 0 && (
              <CommandGroup heading="Azioni rapide">
                {actions.map(({ key, label, icon: Icon }) => (
                  <CommandItem key={key} value={`act-${key}`} onSelect={() => { onOpenChange(false); onCreate(key); }} data-testid={`search-action-${key}`}>
                    <Icon className="mr-2 h-4 w-4 text-slate-400" />{label}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
          <div className="flex items-center gap-4 border-t border-slate-100 px-4 py-2 text-[11px] text-slate-400">
            <span className="flex items-center gap-1"><CornerDownLeft className="h-3 w-3" />per aprire</span>
            <span>Esc per chiudere</span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
};
